import { useId, type ReactNode } from 'react'
import { Badge } from '../badge/Badge'
import { DiscordLogo, Mail, MessageSquareLock, Phone } from '../icon'
import { contactMethodLabel, type ContactChoice, type ContactMethod, type ContactPersonOption } from '../contact-input/contact'
import styles from './ContactPicker.module.css'

export interface ContactPickerProps {
  /** The people who could be the contact, and the methods each has verified. */
  options: ContactPersonOption[]
  value: ContactChoice | null
  onChange: (value: ContactChoice) => void
  /** Heading for the group. Default "Primary contact". */
  label?: string
  description?: ReactNode
  /** Shown under one person's row — e.g. "Link Discord" for the signed-in user. */
  renderExtra?: (option: ContactPersonOption) => ReactNode
  /** Message under the group, e.g. a refused save. */
  error?: string
  disabled?: boolean
  className?: string
}

const ICONS: Record<ContactMethod, ReactNode> = {
  discord: <DiscordLogo size={14} />,
  phone: <Phone size={14} />,
  email: <Mail size={14} />,
  carousel: <MessageSquareLock size={14} />,
}

/**
 * Who to contact about a team: a person on it, by a method they verified
 * themselves. Values are never typed here — a person without a verified method
 * is shown, but can't be picked.
 */
export function ContactPicker({
  options,
  value,
  onChange,
  label = 'Primary contact',
  description,
  renderExtra,
  error,
  disabled,
  className,
}: ContactPickerProps) {
  const id = useId()
  return (
    <fieldset
      className={[styles.group, className].filter(Boolean).join(' ')}
      disabled={disabled}
      aria-describedby={description ? `${id}-desc` : undefined}
    >
      <legend className={styles.legend}>{label}</legend>
      {description && <p id={`${id}-desc`} className={styles.description}>{description}</p>}
      {options.length === 0 && <p className={styles.empty}>Nobody on this team yet.</p>}
      <ul className={styles.people}>
        {options.map(o => {
          const selectedHere = value?.user_id === o.user_id
          return (
            <li key={o.user_id} className={selectedHere ? styles.personSelected : styles.person}>
              <div className={styles.who}>
                <span className={styles.name}>{o.name}</span>
                {o.role && <Badge size="sm">{o.role}</Badge>}
              </div>
              {o.minor ? (
                <span className={styles.none}>Under 18 — can’t be the team’s contact</span>
              ) : o.methods.length === 0 ? (
                <span className={styles.none}>No verified contact method</span>
              ) : (
                <div role="radiogroup" aria-label={`Contact ${o.name} by`} className={styles.methods}>
                  {o.methods.map(m => {
                    const checked = selectedHere && value?.method === m
                    return (
                      <button
                        key={m}
                        type="button"
                        role="radio"
                        aria-checked={checked}
                        aria-label={`${o.name} by ${contactMethodLabel(m)}`}
                        className={checked ? styles.methodActive : styles.method}
                        onClick={() => onChange({ user_id: o.user_id, method: m })}
                      >
                        <span aria-hidden="true" className={checked ? styles.dotOn : styles.dot} />
                        <span aria-hidden="true" className={styles.icon}>{ICONS[m]}</span>
                        {contactMethodLabel(m)}
                      </button>
                    )
                  })}
                </div>
              )}
              {renderExtra?.(o)}
            </li>
          )
        })}
      </ul>
      {error && <span className={styles.error} role="alert">{error}</span>}
    </fieldset>
  )
}
